import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ShoppingBag, ChevronDown, MapPin, ExternalLink, HelpCircle, Info, GraduationCap, User as UserIcon, BookOpen, Settings, Shield, LayoutDashboard, Check, ChefHat, Truck, Calendar, Car, FolderOpen, Timer } from 'lucide-react'
import { useCartStore, useUIStore, useAuthStore } from '@/stores'
import { Button } from '@/components/common/Button'
import StaggeredMenu from './StaggeredMenu'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from "react-i18next";
import { DeliveryStickyHeader } from './DeliveryStickyHeader'

export const Header = () => {
    const { t } = useTranslation();
    const { totalItems } = useCartStore()
    const { toggleCart, isMobileMenuOpen } = useUIStore()
    const { user } = useAuthStore()
    const cartCount = totalItems()

    const location = useLocation()
    const navigate = useNavigate()
    const [showLoginSuccess, setShowLoginSuccess] = useState(false)

    useEffect(() => {
        if (location.state?.loginSuccess) {
            setShowLoginSuccess(true)
            
            // Cleanup login state securely to prevent it from showing again on refresh
            const newState = { ...location.state }
            delete newState.loginSuccess
            navigate(location.pathname, { replace: true, state: newState })
        }
    }, [location.state, location.pathname, navigate])

    useEffect(() => {
        if (showLoginSuccess) {
            const timer = setTimeout(() => setShowLoginSuccess(false), 3000)
            return () => clearTimeout(timer)
        }
    }, [showLoginSuccess])

    // State for hover dropdowns
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const managementRef = useRef<HTMLDivElement>(null);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            
            if (activeDropdown === 'ZARZADZANIE' && 
                managementRef.current && 
                !managementRef.current.contains(target)) {
                setActiveDropdown(null);
            }
        };

        document.removeEventListener('mousedown', handleClickOutside);
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeDropdown]);

    const handleMouseEnter = (menu: string) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setActiveDropdown(menu);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setActiveDropdown(null);
        }, 150); // slight delay to prevent flickering
    };

    const navLinks = [
        { name: 'MENU', href: '/menu' },
        { name: 'KREATOR ZESTAWU', href: '/kreator' },
    ]

    const isAdminOrMod = user?.is_admin || user?.is_moderator
    const isKucharz = user?.is_kucharz
    const isDostawca = user?.is_dostawca
    const isHR = user?.is_admin || user?.is_grafik || user?.is_kadrowa
    const isSystem = user?.is_system === true
    const isAccountant = user?.is_ksiegowa
    const isStaff = user?.is_admin || user?.is_moderator || user?.is_grafik || user?.is_kadrowa || user?.is_kucharz || user?.is_dostawca || user?.is_ksiegowa || user?.is_system

    const userPanelUrl = isStaff ? '/panel-pracownika' : '/moje-konto'
    const showManagementMenu = isAdminOrMod || isKucharz || isDostawca || isSystem || isAccountant || isHR

    const isKitchenOnlyMenu = isKucharz && !isSystem && !isAdminOrMod && !isHR

    const mobileMenuItems = [
        { label: 'MENU', link: '/menu', ariaLabel: 'Menu' },
        { label: 'KREATOR ZESTAWU', link: '/kreator', ariaLabel: 'Kreator' },
        {
            label: 'SUSHIMAT',
            children: [
                { label: 'Zgłoś lokalizację', link: '/zglos-lokalizacje' },
                { label: 'Przejdź do strony', link: 'https://sushimat.com/', external: true }
            ]
        },
        {
            label: 'INFORMACJE',
            children: [
                { label: 'FAQ', link: '/faq' },
                { label: 'O Nas', link: '/o-nas' },
                { label: 'Blog', link: '/blog' },
                { label: 'Akademia', link: '/akademia' },
                { label: 'Kontakt', link: '/kontakt' }
            ]
        },
        // Management links for mobile (role-based + system)
        ...(showManagementMenu ? [{
            label: isKitchenOnlyMenu ? 'KUCHNIA' : isDostawca && !isAdminOrMod && !isSystem ? 'DOSTAWA' : 'ZARZĄDZANIE',
            children: isKitchenOnlyMenu
                ? [{ label: 'Panel Kuchni', link: '/kuchnia' }]
                : isDostawca && !isAdminOrMod && !isSystem && !isHR
                  ? [{ label: 'Panel Dostaw', link: '/dostawa' }, { label: 'Pojazdy', link: '/manage/pojazdy' }]
                : [
                    ...(user?.role === 'admin' || isSystem ? [{ label: '⚙️ Panel Administratora', link: '/admin' }] : []),
                    { label: 'Panel Moderatora', link: '/moderator' },
                    ...(isHR ? [{ label: 'Kadry i Grafik', link: '/manage/kadry' }] : []),
                    ...(isSystem || isAccountant || user?.is_admin ? [{ label: '🗂 Kartoteki Pracowników', link: '/manage/admin/employees' }] : []),
                    ...(isSystem || isAccountant || user?.is_admin ? [{ label: '⏱ Panel RCP', link: '/rcp' }] : []),
                    ...(user?.is_admin || isSystem || isDostawca || isHR ? [{ label: 'Pojazdy', link: '/manage/pojazdy' }] : []),
                    { label: 'Kuchnia', link: '/kuchnia' },
                    { label: 'Dostawa', link: '/dostawa' },
                ],
        }] : []),
        // Auth link for mobile
        user ? { label: isStaff ? 'PANEL PRACOWNIKA' : 'MOJE KONTO', link: userPanelUrl, ariaLabel: 'Moje Konto' }
            : { label: 'ZALOGUJ SIĘ', link: '/login', ariaLabel: 'Zaloguj się' }
    ];

    const socialItems = [
        { label: 'Facebook', link: 'https://www.facebook.com/sushikingns' },
        { label: 'Instagram', link: 'https://www.instagram.com/sushikingns' }
    ];

    return (
        <header className="sticky top-0 z-[2000] w-full min-h-[64px] border-b border-white/20 bg-white/60 backdrop-blur-xl shadow-lg supports-[backdrop-filter]:bg-white/30">
            <div className={`container mx-auto px-4 h-16 flex items-center ${isMobileMenuOpen ? 'justify-end' : 'justify-between'}`}>
                {/* Logo */}
                {!isMobileMenuOpen && (
                    <Link to="/" className="flex items-center space-x-2 z-50" aria-label="Sushi King - Strona główna">
                        <img loading="lazy" src="/images/logo.avif" alt="Sushi King Logo" className="h-10 w-auto drop-shadow-md" width="120" height="40" />
                    </Link>
                )}

                {/* Desktop Navigation */}
                <nav className="hidden lg:flex items-center space-x-8">
                    {/* Standard Links */}
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.href}
                            className="text-sm font-bold tracking-wider text-gray-800 hover:text-primary transition-colors duration-300 relative group"
                        >
                            {link.name}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                        </Link>
                    ))}

                    {/* SUSHIMAT Dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => handleMouseEnter('sushimat')}
                        onMouseLeave={handleMouseLeave}
                    >
                        <button 
                            aria-haspopup="true" 
                            aria-expanded={activeDropdown === 'sushimat'}
                            className="flex items-center gap-1 text-sm font-bold tracking-wider text-gray-800 hover:text-primary transition-colors duration-300 group py-4"
                        >
                            {t('sushimat')}<ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'sushimat' ? 'rotate-180' : ''}`} />
                        </button>

                        {activeDropdown === 'sushimat' && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                                <div className="w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                    <Link
                                        to="/zglos-lokalizacje"
                                        className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 group"
                                    >
                                        <div className="bg-red-50 p-2 rounded-lg text-primary group-hover:scale-110 transition-transform">
                                            <MapPin className="w-5 h-5" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-800 text-sm">{t('zglosLokalizacje')}</span>
                                            <span className="text-xs text-gray-500">{t('zaproponujNoweMiejsce')}</span>
                                        </div>
                                    </Link>
                                    <a
                                        href="https://sushimat.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors group"
                                    >
                                        <div className="bg-blue-50 p-2 rounded-lg text-blue-600 group-hover:scale-110 transition-transform">
                                            <ExternalLink className="w-5 h-5" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-800 text-sm">{t('przejdzDoStrony')}</span>
                                            <span className="text-xs text-gray-500">{t('sushimatcom')}</span>
                                        </div>
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* INFORMACJE Dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => handleMouseEnter('INFORMACJE')}
                        onMouseLeave={handleMouseLeave}
                    >
                        <button 
                            aria-haspopup="true" 
                            aria-expanded={activeDropdown === 'INFORMACJE'}
                            className="flex items-center gap-1 text-sm font-bold tracking-wider text-gray-800 hover:text-primary transition-colors duration-300 group py-4"
                        >
                            {t('informacje')}<ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'INFORMACJE' ? 'rotate-180' : ''}`} />
                        </button>

                        {activeDropdown === 'INFORMACJE' && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                                <div className="w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                    <Link to="/faq" className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 group">
                                        <div className="bg-yellow-50 p-2 rounded-lg text-yellow-600 group-hover:scale-110 transition-transform">
                                            <HelpCircle className="w-5 h-5" />
                                        </div>
                                        <span className="font-bold text-gray-800 text-sm">{t('faq')}</span>
                                    </Link>
                                    <Link to="/o-nas" className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 group">
                                        <div className="bg-purple-50 p-2 rounded-lg text-purple-600 group-hover:scale-110 transition-transform">
                                            <Info className="w-5 h-5" />
                                        </div>
                                        <span className="font-bold text-gray-800 text-sm">{t('oNas')}</span>
                                    </Link>
                                    <Link to="/blog" className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 group">
                                        <div className="bg-orange-50 p-2 rounded-lg text-orange-600 group-hover:scale-110 transition-transform">
                                            <BookOpen className="w-5 h-5" />
                                        </div>
                                        <span className="font-bold text-gray-800 text-sm">{t('blog')}</span>
                                    </Link>
                                    <Link to="/akademia" className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 group">
                                        <div className="bg-green-50 p-2 rounded-lg text-green-600 group-hover:scale-110 transition-transform">
                                            <GraduationCap className="w-5 h-5" />
                                        </div>
                                        <span className="font-bold text-gray-800 text-sm">{t('akademiaSushiKing')}</span>
                                    </Link>
                                    <Link to="/kontakt" className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors group">
                                        <div className="bg-gray-50 p-2 rounded-lg text-gray-600 group-hover:scale-110 transition-transform">
                                            <MapPin className="w-5 h-5" />
                                        </div>
                                        <span className="font-bold text-gray-800 text-sm">{t('kontakt')}</span>
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ZARZĄDZANIE / KUCHNIA / DOSTAWA */}
                    {showManagementMenu && (
                        <div className="relative" ref={managementRef}>
                            <button
                                onClick={() => setActiveDropdown(prev => prev === 'ZARZADZANIE' ? null : 'ZARZADZANIE')}
                                aria-haspopup="true" 
                                aria-expanded={activeDropdown === 'ZARZADZANIE'}
                                className="flex items-center gap-1 text-sm font-bold tracking-wider text-primary hover:text-primary/80 transition-colors duration-300 py-4"
                            >
                                {isKucharz && !isSystem && !isAdminOrMod && !isHR ? 'KUCHNIA' : isDostawca && !isAdminOrMod && !isSystem && !isHR ? 'DOSTAWA' : 'ZARZĄDZANIE'}<ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'ZARZADZANIE' ? 'rotate-180' : ''}`} />
                            </button>

                            {activeDropdown === 'ZARZADZANIE' && (
                                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-20">
                                    <div className="w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                        <div className="flex flex-col">
                                            {(user?.is_admin || isSystem) && (
                                                <Link
                                                    to="/admin"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-red-50 transition-colors border-b border-gray-100 group"
                                                >
                                                    <div className="bg-red-50 p-2 rounded-lg text-primary group-hover:scale-110 transition-transform">
                                                        <Shield className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Panel Administratora</span>
                                                        <span className="text-xs text-gray-500">Raporty, konfiguracja</span>
                                                    </div>
                                                </Link>
                                            )}
                                            
                                            {(isAdminOrMod || isSystem) && (
                                                <Link
                                                    to="/moderator"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 group"
                                                >
                                                    <div className="bg-gray-100 p-2 rounded-lg text-gray-600 group-hover:scale-110 transition-transform">
                                                        <Settings className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Panel Moderatora</span>
                                                        <span className="text-xs text-gray-500">Produkty, zamówienia, treści</span>
                                                    </div>
                                                </Link>
                                            )}

                                            {isHR && (
                                                <Link
                                                    to="/manage/kadry"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-green-50 transition-colors border-b border-gray-100 group"
                                                >
                                                    <div className="bg-green-50 p-2 rounded-lg text-green-600 group-hover:scale-110 transition-transform">
                                                        <Calendar className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Kadry i Grafik</span>
                                                        <span className="text-xs text-gray-500">Zmiany, dyspozycyjność, szablony</span>
                                                    </div>
                                                </Link>
                                            )}

                                            {(isSystem || isAccountant || user?.is_admin) && (
                                                <Link
                                                    to="/manage/admin/employees"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-emerald-50 transition-colors border-b border-gray-100 group"
                                                >
                                                    <div className="bg-emerald-50 p-2 rounded-lg text-emerald-700 group-hover:scale-110 transition-transform">
                                                        <FolderOpen className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Kartoteki Pracowników</span>
                                                        <span className="text-xs text-gray-500">Dane personelu, umowy</span>
                                                    </div>
                                                </Link>
                                            )}

                                            {(isSystem || isAccountant || user?.is_admin) && (
                                                <Link
                                                    to="/rcp"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-teal-50 transition-colors border-b border-gray-100 group"
                                                >
                                                    <div className="bg-teal-50 p-2 rounded-lg text-teal-700 group-hover:scale-110 transition-transform">
                                                        <Timer className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Panel RCP</span>
                                                        <span className="text-xs text-gray-500">Czas pracy, historia odbić</span>
                                                    </div>
                                                </Link>
                                            )}

                                            {(user?.is_admin || isSystem || isDostawca || isHR) && (
                                                <Link
                                                    to="/manage/pojazdy"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-amber-50 transition-colors border-b border-gray-100 group"
                                                >
                                                    <div className="bg-amber-50 p-2 rounded-lg text-amber-600 group-hover:scale-110 transition-transform">
                                                        <Car className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Pojazdy</span>
                                                        <span className="text-xs text-gray-500">Terminy, OC, przeglądy</span>
                                                    </div>
                                                </Link>
                                            )}

                                            {(isKucharz || isAdminOrMod || isSystem) && (
                                                <Link
                                                    to="/kuchnia"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-orange-50 transition-colors border-b border-gray-100 group"
                                                >
                                                    <div className="bg-orange-50 p-2 rounded-lg text-orange-600 group-hover:scale-110 transition-transform">
                                                        <ChefHat className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Kuchnia</span>
                                                        <span className="text-xs text-gray-500">Zamówienia bieżące</span>
                                                    </div>
                                                </Link>
                                            )}

                                            {(isDostawca || isAdminOrMod || isSystem) && (
                                                <Link
                                                    to="/dostawa"
                                                    onClick={() => setActiveDropdown(null)}
                                                    className="flex items-center gap-3 p-4 hover:bg-blue-50 transition-colors group"
                                                >
                                                    <div className="bg-blue-50 p-2 rounded-lg text-blue-600 group-hover:scale-110 transition-transform">
                                                        <Truck className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-800 text-sm">Dostawa</span>
                                                        <span className="text-xs text-gray-500">Statusy dostaw i kierowcy</span>
                                                    </div>
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </nav>

                {/* Actions */}
                <div className="flex items-center space-x-3">
                    {/* Desktop Login/Profile Button */}
                    <div className="hidden md:flex items-center gap-3 z-50">
                        {user ? (
                            <div className="relative">
                                <Link to={userPanelUrl}>
                                    <Button variant="ghost" className="font-medium hover:bg-white/40 flex items-center gap-2">
                                        <LayoutDashboard className="w-5 h-5" />
                                        {isStaff ? 'Panel Pracownika' : t('mojeKonto')}
                                    </Button>
                                </Link>
                                
                                {/* Clickable Success Tooltip */}
                                {showLoginSuccess && (
                                    <Link 
                                        to={userPanelUrl}
                                        className="absolute top-full left-1/2 -translate-x-1/2 mt-3 flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 backdrop-blur-md text-green-700 rounded-xl shadow-lg border border-green-200 animate-in fade-in slide-in-from-top-2 duration-500 whitespace-nowrap cursor-pointer before:content-[''] before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:border-8 before:border-transparent before:border-b-green-200 transition-colors z-[100]"
                                        onClick={() => setShowLoginSuccess(false)}
                                    >
                                        <div className="bg-green-100 p-1 rounded-full text-green-600 flex-shrink-0">
                                            <Check className="w-3 h-3" />
                                        </div>
                                        <span className="font-bold text-sm tracking-wide">{t('zalogowano')}</span>
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <Link to="/login">
                                <Button variant="ghost" className="font-medium hover:bg-white/40">
                                    {t('zalogujSie')}
                                </Button>
                            </Link>
                        )}
                    </div>

                    {/* Desktop Cart Icon */}
                    {!(['/koszyk', '/zamowienie'].includes(location.pathname) || location.pathname.startsWith('/zamowienie/')) && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleCart}
                            className="hidden md:flex relative z-50 text-gray-800 hover:bg-gray-100/50"
                            aria-label={`Koszyk${cartCount > 0 ? `, ${cartCount} produktów` : ''}`}
                        >
                            <ShoppingBag className="h-6 w-6" />
                            {cartCount > 0 && (
                                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-black text-white flex items-center justify-center shadow-sm">
                                    {cartCount}
                                </span>
                            )}
                        </Button>
                    )}
                    {/* Mobile Login/Profile Icon */}
                    {!isMobileMenuOpen && (
                        <Link to={user ? userPanelUrl : "/login"} className="lg:hidden" aria-label={user ? "Moje konto" : "Zaloguj się"}>
                            <Button variant="ghost" size="icon" className="text-gray-800 hover:bg-gray-100" aria-label={user ? "Moje konto" : "Zaloguj się"}>
                                <UserIcon className="h-6 w-6" />
                            </Button>
                        </Link>
                    )}

                    <div className="lg:hidden">
                        <StaggeredMenu
                            items={mobileMenuItems}
                            socialItems={socialItems}
                            colors={['#D32F2F', '#B71C1C', '#EF5350']} // Sushi King Reds
                            menuButtonColor="#000"
                            openMenuButtonColor="#000"
                            logoUrl="/images/logo.avif"
                        />
                    </div>
                </div>
            </div>
            {location.pathname.startsWith('/menu') && <DeliveryStickyHeader />}
        </header>
    )
}
